import { useMemo } from 'react';
import { useQuery, UseQueryOptions } from 'react-query';
import AffordableQualificationDocumentService from '../../services/affordableQualificationDocumentService';
import KpiService from '../../services/kpiService';
import { COMPLIANCE_TYPES } from '../../containers/ComplianceOverview/constants';

const useComplianceOverviewQuery = ({
  organizationId,
  complianceType,
  options,
  includeNotStartedCerts,
  page,
  perPage,
  sortBy,
  sortOrder,
  filter,
  searchText,
}: {
  organizationId: string,
  complianceType: string,
  options?: UseQueryOptions,
  includeNotStartedCerts?: boolean,
  page?: number,
  perPage?: number,
  sortBy?: string,
  sortOrder?: string,
  filter?: Object,
  searchText?: string,
}) => {
  const isQueryEnabled = options?.enabled ?? true;
  const isAllOpenCerts = complianceType === COMPLIANCE_TYPES.ALL_OPEN_CERTS;

  const queryKey = [
    'useComplianceOverviewQuery',
    organizationId,
    complianceType,
    includeNotStartedCerts,
    // Left off the Needs Review key so All Open Certs table-state changes
    // don't cause a spurious refetch while that tab isn't active.
    ...(isAllOpenCerts
      ? [page, perPage, sortBy, sortOrder, filter, searchText]
      : []),
  ];

  const { data, isLoading, isFetching } = useQuery(
    queryKey,
    async ({ signal } = {}) => {
      const requestOptions = signal ? { signal } : undefined;
      let response = [];
      if (complianceType === COMPLIANCE_TYPES.NEEDS_REVIEW) {
        response =
          await new AffordableQualificationDocumentService().getComplianceDocumentsNeedsReview(
            organizationId,
            requestOptions,
          );
      }

      if (isAllOpenCerts) {
        response = await new KpiService().getComplianceOverviewOpenCerts(
          organizationId,
          includeNotStartedCerts,
          { page, perPage, sortBy, sortOrder, filter, searchText },
          requestOptions,
        );
      }

      return response;
    },
    {
      enabled: Boolean(organizationId && complianceType && isQueryEnabled),
      staleTime: 60000,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      retry: false,
      ...options,
    },
  );

  // keepPreviousData hands the other tab's payload over on a complianceType
  // switch, so check the shape rather than trusting the tab.
  const results = useMemo(() => {
    if (isAllOpenCerts) {
      return Array.isArray(data?.results) ? data.results : [];
    }
    return Array.isArray(data) ? data : [];
  }, [data, isAllOpenCerts]);

  return {
    results,
    isLoading,
    isFetching,
    totalCount: isAllOpenCerts ? data?.totalCount ?? 0 : undefined,
    page: isAllOpenCerts ? data?.page ?? page ?? 1 : undefined,
    perPage: isAllOpenCerts ? data?.perPage ?? perPage : undefined,
    pageCount: isAllOpenCerts ? data?.pageCount ?? 0 : undefined,
    asOf: isAllOpenCerts ? data?.asOf : undefined,
  };
};

export default useComplianceOverviewQuery;
